import React, { createContext, FC, memo, ReactNode, useContext, useMemo, useState } from 'react';
import pick from 'lodash/pick';
import moment from 'moment';

import { IPayPeriodWeekDTO } from 'src/api/dtos/payPeriodWeek.dto';
import TimesheetRepository from 'src/api/repositories/ess/v1/timesheet.repository';
import { API_DATE_FORMAT } from 'src/shared/constants';
import { TimesheetSummaryTypeEnums, WeekTypeEnums } from 'src/shared/enums';
import { useNavigateByQueryParams, useQuery } from 'src/shared/hooks';
import { parseQuery } from 'src/shared/utils';

type TimesheetQueryProps = {
  selectedPayPeriodWeek: IPayPeriodWeekDTO | undefined;
  payPeriodWeeks: IPayPeriodWeekDTO[];
  type: TimesheetSummaryTypeEnums;
  showDepartment: boolean;
  showJobs: boolean;
  showExpenses: boolean;
  onPayPeriodWeekChange: (payPeriodWeek: IPayPeriodWeekDTO) => void;
  onTypeChange: (type: TimesheetSummaryTypeEnums) => void;
  onShowDepartmentToggle: () => void;
  onShowJobsToggle: () => void;
  onShowExpensesToggle: () => void;
};

const TimesheetQueryContext = createContext<TimesheetQueryProps>({} as TimesheetQueryProps);

export const useTimesheetQuery = (): TimesheetQueryProps => useContext<TimesheetQueryProps>(TimesheetQueryContext);

export const TimesheetQueryProvider: FC<{ children: ReactNode }> = memo(({ children }) => {
  const query = useQuery();
  const navigateByQueryParams = useNavigateByQueryParams();

  const [type, setType] = useState(TimesheetSummaryTypeEnums.WEEKLY);
  const [showDepartment, setShowDepartment] = useState(true);
  const [showJobs, setShowJobs] = useState(true);
  const [showExpenses, setShowExpenses] = useState(true);

  const { data: payPeriodWeeks } = TimesheetRepository.endpoints.useGetTimesheetPayPeriodWeeksQuery(undefined, {
    selectFromResult: ({ data, ...results }) => ({ data: data ?? [], ...results }),
  });

  const selectedPayPeriodWeek = useMemo<IPayPeriodWeekDTO | undefined>(() => {
    if (!payPeriodWeeks.length) {
      return;
    }

    // Find selected pay period week from query params
    const q = query ? pick<IPayPeriodWeekDTO>(parseQuery(query.toString()), ['startDate', 'endDate']) : {};
    const queryPayPeriodWeek = payPeriodWeeks.find(
      (payPeriodWeek) =>
        moment(q?.startDate).isSame(payPeriodWeek.startDate, 'day') &&
        moment(q?.endDate).isSame(payPeriodWeek.endDate, 'day'),
    );

    // Default to current pay period week
    const currentPayPeriodWeek = payPeriodWeeks.find(
      (payPeriodWeek) => payPeriodWeek.weekType === WeekTypeEnums.CURRENT,
    );

    return queryPayPeriodWeek ?? currentPayPeriodWeek;
  }, [query, payPeriodWeeks]);

  const handlePayPeriodWeekChange = (payPeriodWeek: IPayPeriodWeekDTO) => {
    navigateByQueryParams({
      startDate: moment(payPeriodWeek.startDate).format(API_DATE_FORMAT),
      endDate: moment(payPeriodWeek.endDate).format(API_DATE_FORMAT),
    });
  };

  const handleTypeChange = (type: TimesheetSummaryTypeEnums) => {
    setType(type);
  };

  const handleShowDepartmentToggle = () => {
    setShowDepartment(!showDepartment);
  };

  const handleShowJobsToggle = () => {
    setShowJobs(!showJobs);
  };

  const handleShowExpensesToggle = () => {
    setShowExpenses(!showExpenses);
  };

  return (
    <TimesheetQueryContext.Provider
      value={{
        payPeriodWeeks,
        selectedPayPeriodWeek,
        type,
        showDepartment,
        showJobs,
        showExpenses,
        onPayPeriodWeekChange: handlePayPeriodWeekChange,
        onTypeChange: handleTypeChange,
        onShowDepartmentToggle: handleShowDepartmentToggle,
        onShowJobsToggle: handleShowJobsToggle,
        onShowExpensesToggle: handleShowExpensesToggle,
      }}
    >
      {children}
    </TimesheetQueryContext.Provider>
  );
});
